// Decide which status icon a row in the producer "Assignments" compliance
// table should show. Pulled out as a pure function so the precedence is
// unit-testable (the surrounding JSX isn't) and documented in one place.
//
// The key rule this encodes — and the bug it fixes — is that an explicit
// MANUAL completion ("marked as done" in the crew app) is ground truth and
// must always read as done. Previously an "unknown" GPS-metadata state
// (matchConfidence.source === null) was checked before the manual flag, so a
// completed assignment with no/odd GPS metadata — most obviously a task with
// no location/stage to match against — showed a grey "?" instead of a green
// check, i.e. "not processed as completed".

export type ComplianceIconKind =
  | "override" // producer manually adjusted — caller adds the pencil marker
  | "present" // done / attended (green)
  | "drift" // attended but GPS placed them just outside the polygon (yellow)
  | "unknown" // no GPS metadata to judge by, and not manually completed (grey)
  | "absent"; // not attended and not completed (red)

export interface ComplianceStatusInput {
  /** Final present flag (already folds in override + manual completion). */
  present: boolean;
  /** Crew member ticked this assignment off in the app. */
  markedAsDone: boolean;
  /** Producer manually overrode the presence for this assignment. */
  hasOverride: boolean;
  /** GPS match confidence source; null = no usable metadata. */
  matchSource: "strict" | "within_accuracy" | "no_presence" | null;
}

export function resolveComplianceIconKind(input: ComplianceStatusInput): ComplianceIconKind {
  if (input.hasOverride) return "override";

  const isDrift = input.matchSource === "within_accuracy";

  // Manual completion wins over an unknown/absent GPS reading: if the crew
  // said they did it, it's done — even for a location-less task the GPS can
  // never confirm.
  if (input.markedAsDone) return isDrift ? "drift" : "present";

  if (input.matchSource === null) return "unknown";
  if (!input.present) return "absent";
  return isDrift ? "drift" : "present";
}
